interface DiagramEntry {
  figure: HTMLElement;
  scrollRegion: HTMLElement;
  svg: SVGSVGElement;
  trigger: HTMLButtonElement;
  title: string;
  description: string;
  downloadHref: string | null;
}

interface ActiveDiagram {
  entry: DiagramEntry;
  placeholder: Comment;
  originalAriaLabel: string | null;
  originalScrollLeft: number;
  originalScrollTop: number;
  originalSvgWidth: string;
  originalSvgHeight: string;
  baseWidth: number;
  baseHeight: number;
  zoom: number;
}

interface DiagramGalleryOptions {
  beforeOpen?: () => void;
}

interface DiagramPan {
  pointerId: number;
  scrollRegion: HTMLElement;
  startClientX: number;
  startClientY: number;
  startScrollLeft: number;
  startScrollTop: number;
}

interface DiagramZoomAnchor {
  viewportX: number;
  viewportY: number;
}

let entries: DiagramEntry[] = [];
let activeDiagram: ActiveDiagram | null = null;
let activePan: DiagramPan | null = null;
let galleryOpener: HTMLButtonElement | null = null;
let beforeOpen: (() => void) | undefined;

export function initializeDiagramGallery(options: DiagramGalleryOptions = {}) {
  beforeOpen = options.beforeOpen;
  entries = [...document.querySelectorAll<HTMLElement>(".q-diagram")]
    .map(readDiagramEntry)
    .filter((entry): entry is DiagramEntry => entry !== null);
  if (entries.length === 0) return;

  requireGallery();
}

document.addEventListener("click", event => {
  const target = event.target as Element | null;
  const trigger = target?.closest<HTMLButtonElement>("[data-open-diagram-gallery]");
  if (trigger) {
    const entryIndex = entries.findIndex(entry => entry.trigger === trigger);
    if (entryIndex < 0) return;
    event.preventDefault();
    beforeOpen?.();
    galleryOpener = trigger;
    openDiagram(entryIndex);
    return;
  }

  if (target?.closest("[data-close-diagram-gallery]")) {
    getGallery()?.close();
  } else if (target?.closest("[data-previous-diagram]")) {
    showAdjacentDiagram(-1);
  } else if (target?.closest("[data-next-diagram]")) {
    showAdjacentDiagram(1);
  } else if (target?.closest("[data-diagram-zoom-out]")) {
    setZoom((activeDiagram?.zoom ?? 1) / 1.25);
  } else if (target?.closest("[data-diagram-zoom-in]")) {
    setZoom((activeDiagram?.zoom ?? 1) * 1.25);
  } else if (target?.closest("[data-diagram-fit-width]")) {
    fitDiagramWidth();
  }
});

window.addEventListener("beforeprint", () => {
  const gallery = getGallery();
  if (gallery?.open) gallery.close();
});

function readDiagramEntry(figure: HTMLElement): DiagramEntry | null {
  const scrollRegion = figure.querySelector<HTMLElement>(":scope > .q-diagram-scroll");
  const svg = scrollRegion?.querySelector<SVGSVGElement>(":scope > svg");
  const trigger = figure.querySelector<HTMLButtonElement>("[data-open-diagram-gallery]");
  if (!scrollRegion || !svg || !trigger) return null;

  return {
    figure,
    scrollRegion,
    svg,
    trigger,
    title: figure.querySelector<HTMLElement>(":scope > figcaption > strong")?.textContent?.trim()
      || "Diagram",
    description: figure.querySelector<HTMLElement>(":scope > figcaption > .q-diagram-description")?.textContent?.trim()
      || "",
    downloadHref: figure.querySelector<HTMLAnchorElement>("[data-download-diagram-png]")?.href ?? null
  };
}

function requireGallery(): HTMLDialogElement {
  const existing = getGallery();
  if (existing) return existing;

  const gallery = document.createElement("dialog");
  gallery.id = "qprins-diagram-gallery";
  gallery.className = "q-diagram-gallery";
  gallery.setAttribute("aria-labelledby", "qprins-diagram-gallery-title");
  gallery.innerHTML = `
    <div class="q-diagram-gallery__shell">
      <header class="q-diagram-gallery__header">
        <div class="q-diagram-gallery__identity">
          <p class="eyebrow">Diagram <span data-diagram-gallery-count></span></p>
          <h2 id="qprins-diagram-gallery-title" data-diagram-gallery-title></h2>
          <p class="q-diagram-gallery__description" data-diagram-gallery-description hidden></p>
        </div>
        <div class="q-diagram-gallery__controls" aria-label="Diagram viewer controls">
          <button type="button" class="q-diagram-gallery__icon" data-previous-diagram aria-label="Previous diagram" title="Previous diagram">&#x2190;</button>
          <button type="button" class="q-diagram-gallery__icon" data-next-diagram aria-label="Next diagram" title="Next diagram">&#x2192;</button>
          <span class="q-diagram-gallery__divider" aria-hidden="true"></span>
          <button type="button" class="q-diagram-gallery__icon" data-diagram-zoom-out aria-label="Zoom out" title="Zoom out">&#x2212;</button>
          <output class="q-diagram-gallery__zoom" data-diagram-zoom aria-live="polite">100%</output>
          <button type="button" class="q-diagram-gallery__icon" data-diagram-zoom-in aria-label="Zoom in" title="Zoom in">+</button>
          <button type="button" class="q-diagram-gallery__control" data-diagram-fit-width>Fit width</button>
          <a class="q-diagram-gallery__control" data-diagram-gallery-download download>Download PNG</a>
          <button type="button" class="q-diagram-gallery__close" data-close-diagram-gallery>Close</button>
        </div>
      </header>
      <div class="q-diagram-gallery__stage" data-diagram-gallery-stage></div>
    </div>`;
  gallery.addEventListener("close", closeGallery);
  gallery.addEventListener("click", event => {
    if (event.target === gallery) gallery.close();
  });
  const stage = getRequiredElement<HTMLElement>(gallery, "[data-diagram-gallery-stage]");
  stage.addEventListener("pointerdown", beginDiagramPan);
  stage.addEventListener("pointermove", moveDiagramPan);
  stage.addEventListener("pointerup", endDiagramPan);
  stage.addEventListener("pointercancel", endDiagramPan);
  stage.addEventListener("lostpointercapture", endDiagramPan);
  stage.addEventListener("wheel", zoomDiagramWithWheel, { passive: false });
  document.body.append(gallery);
  return gallery;
}

function openDiagram(index: number) {
  const gallery = requireGallery();
  if (gallery.open) restoreActiveDiagram();
  const entry = entries[index];
  if (!entry) return;

  const placeholder = document.createComment("qprins-diagram-gallery-placeholder");
  entry.scrollRegion.replaceWith(placeholder);
  const viewBox = entry.svg.viewBox.baseVal;
  const baseWidth = viewBox.width || entry.svg.width.baseVal.value;
  const baseHeight = viewBox.height || entry.svg.height.baseVal.value;
  activeDiagram = {
    entry,
    placeholder,
    originalAriaLabel: entry.scrollRegion.getAttribute("aria-label"),
    originalScrollLeft: entry.scrollRegion.scrollLeft,
    originalScrollTop: entry.scrollRegion.scrollTop,
    originalSvgWidth: entry.svg.style.width,
    originalSvgHeight: entry.svg.style.height,
    baseWidth,
    baseHeight,
    zoom: 1
  };

  entry.scrollRegion.setAttribute(
    "aria-label",
    `${entry.title}, full-screen diagram; drag to pan or scroll to explore`);
  getRequiredElement<HTMLElement>(gallery, "[data-diagram-gallery-stage]").replaceChildren(entry.scrollRegion);
  getRequiredElement<HTMLElement>(gallery, "[data-diagram-gallery-title]").textContent = entry.title;
  const description = getRequiredElement<HTMLElement>(gallery, "[data-diagram-gallery-description]");
  description.textContent = entry.description;
  description.hidden = entry.description.length === 0;
  getRequiredElement<HTMLElement>(gallery, "[data-diagram-gallery-count]").textContent =
    entries.length > 1 ? `${index + 1} of ${entries.length}` : "";
  const download = getRequiredElement<HTMLAnchorElement>(gallery, "[data-diagram-gallery-download]");
  if (entry.downloadHref) {
    download.href = entry.downloadHref;
    download.hidden = false;
  } else {
    download.removeAttribute("href");
    download.hidden = true;
  }
  const previous = getRequiredElement<HTMLButtonElement>(gallery, "[data-previous-diagram]");
  const next = getRequiredElement<HTMLButtonElement>(gallery, "[data-next-diagram]");
  previous.hidden = entries.length < 2;
  next.hidden = entries.length < 2;

  document.documentElement.dataset.diagramGalleryOpen = "true";
  if (!gallery.open) gallery.showModal();
  requestAnimationFrame(() => {
    if (activeDiagram?.entry !== entry) return;
    fitDiagramWidth();
    entry.scrollRegion.focus({ preventScroll: true });
  });
}

function showAdjacentDiagram(offset: number) {
  if (!activeDiagram || entries.length < 2) return;
  const current = entries.indexOf(activeDiagram.entry);
  const next = (current + offset + entries.length) % entries.length;
  openDiagram(next);
}

function fitDiagramWidth() {
  if (!activeDiagram) return;
  const availableWidth = Math.max(1, activeDiagram.entry.scrollRegion.clientWidth - 48);
  const fitZoom = availableWidth / activeDiagram.baseWidth;
  setZoom(fitZoom, Math.min(0.25, fitZoom));
  activeDiagram.entry.scrollRegion.scrollTo({ left: 0, top: 0 });
}

function setZoom(
  requestedZoom: number,
  minimumZoom = Math.min(0.25, activeDiagram?.zoom ?? 0.25),
  anchor?: DiagramZoomAnchor) {
  if (!activeDiagram || !Number.isFinite(requestedZoom)) return;
  const { entry, baseWidth, baseHeight, zoom: previousZoom } = activeDiagram;
  const nextZoom = Math.min(3, Math.max(minimumZoom, requestedZoom));
  const viewportX = anchor?.viewportX ?? entry.scrollRegion.clientWidth / 2;
  const viewportY = anchor?.viewportY ?? entry.scrollRegion.clientHeight / 2;
  const diagramX = (entry.scrollRegion.scrollLeft + viewportX) / previousZoom;
  const diagramY = (entry.scrollRegion.scrollTop + viewportY) / previousZoom;
  activeDiagram.zoom = nextZoom;
  entry.svg.style.width = `${Math.round(baseWidth * nextZoom)}px`;
  entry.svg.style.height = `${Math.round(baseHeight * nextZoom)}px`;
  const gallery = getGallery();
  if (gallery) {
    getRequiredElement<HTMLOutputElement>(gallery, "[data-diagram-zoom]").textContent =
      `${Math.round(nextZoom * 100)}%`;
  }
  entry.scrollRegion.scrollTo({
    left: Math.max(0, diagramX * nextZoom - viewportX),
    top: Math.max(0, diagramY * nextZoom - viewportY)
  });
}

function zoomDiagramWithWheel(event: WheelEvent) {
  if ((!event.ctrlKey && !event.metaKey) || !activeDiagram) return;
  const target = event.target as Element | null;
  const scrollRegion = target?.closest<HTMLElement>(".q-diagram-scroll");
  if (scrollRegion !== activeDiagram.entry.scrollRegion) return;

  event.preventDefault();
  const rect = scrollRegion.getBoundingClientRect();
  const delta = event.deltaY * (event.deltaMode === WheelEvent.DOM_DELTA_LINE
    ? 16
    : event.deltaMode === WheelEvent.DOM_DELTA_PAGE
      ? Math.max(1, scrollRegion.clientHeight)
      : 1);
  setZoom(
    activeDiagram.zoom * Math.exp(-delta * 0.002),
    Math.min(0.25, activeDiagram.zoom),
    {
      viewportX: event.clientX - rect.left,
      viewportY: event.clientY - rect.top
    });
}

function beginDiagramPan(event: PointerEvent) {
  if (event.button !== 0 || event.isPrimary === false) return;
  const target = event.target as Element | null;
  const scrollRegion = target?.closest<HTMLElement>(".q-diagram-scroll");
  if (!scrollRegion || activeDiagram?.entry.scrollRegion !== scrollRegion) return;

  stopDiagramPan();
  activePan = {
    pointerId: event.pointerId,
    scrollRegion,
    startClientX: event.clientX,
    startClientY: event.clientY,
    startScrollLeft: scrollRegion.scrollLeft,
    startScrollTop: scrollRegion.scrollTop
  };
  scrollRegion.dataset.diagramPanning = "true";
  scrollRegion.setPointerCapture(event.pointerId);
  scrollRegion.focus({ preventScroll: true });
  event.preventDefault();
}

function moveDiagramPan(event: PointerEvent) {
  const pan = activePan;
  if (!pan || pan.pointerId !== event.pointerId) return;

  pan.scrollRegion.scrollTo({
    left: pan.startScrollLeft - (event.clientX - pan.startClientX),
    top: pan.startScrollTop - (event.clientY - pan.startClientY)
  });
  event.preventDefault();
}

function endDiagramPan(event: PointerEvent) {
  stopDiagramPan(event.pointerId);
}

function stopDiagramPan(pointerId?: number) {
  const pan = activePan;
  if (!pan || (pointerId !== undefined && pan.pointerId !== pointerId)) return;

  activePan = null;
  delete pan.scrollRegion.dataset.diagramPanning;
  if (pan.scrollRegion.hasPointerCapture(pan.pointerId)) {
    pan.scrollRegion.releasePointerCapture(pan.pointerId);
  }
}

function closeGallery() {
  restoreActiveDiagram();
  delete document.documentElement.dataset.diagramGalleryOpen;
  const opener = galleryOpener;
  galleryOpener = null;
  if (opener?.isConnected) opener.focus({ preventScroll: true });
}

function restoreActiveDiagram() {
  stopDiagramPan();
  const active = activeDiagram;
  if (!active) return;
  activeDiagram = null;
  const { entry } = active;
  active.placeholder.replaceWith(entry.scrollRegion);
  if (active.originalAriaLabel === null) entry.scrollRegion.removeAttribute("aria-label");
  else entry.scrollRegion.setAttribute("aria-label", active.originalAriaLabel);
  entry.svg.style.width = active.originalSvgWidth;
  entry.svg.style.height = active.originalSvgHeight;
  entry.scrollRegion.scrollTo({ left: active.originalScrollLeft, top: active.originalScrollTop });
}

function getGallery() {
  return document.getElementById("qprins-diagram-gallery") as HTMLDialogElement | null;
}

function getRequiredElement<T extends Element>(root: ParentNode, selector: string): T {
  const element = root.querySelector<T>(selector);
  if (!element) throw new Error(`Missing diagram gallery element: ${selector}`);
  return element;
}
